import React from 'react';
import dom from "react-dom/client";
import ReactCodeMirror from "@uiw/react-codemirror";
import {materialDark as dark, materialLight as light} from '@uiw/codemirror-themes-all';
import {html} from "@codemirror/lang-html";

import LatchButton, {LatchController} from "../components/latch-button";
import {iframeCx, selectElement} from "./wireframe-editor";
import {Popup} from "../components/popups";

export default function CodeEditor() {
	const getIframe = React.useContext(iframeCx)!;

	const abort = new AbortController();
	const latch = new LatchController();

	latch.bind({
		async onLatch() {
			const iframe = getIframe();

			const element = await selectElement(iframe, abort.signal);
			const backup = element.cloneNode(true) as HTMLElement;

			latch.unlatch();

			Popup({
				id: 'element-editor',
				children: <>
					<button className="symbolic close-btn" data-icon={"\ue5cd"}
						// @ts-ignore
						command="request-close"
						commandfor="element-editor"/>

					<EditorForm original={element} onSubmit={source => element.outerHTML = source}
								onPreviewStart={source => {
									backup.outerHTML = source;
									element.replaceWith(backup);
								}} onPreviewEnd={() => backup.replaceWith(element)} />
				</>
			})
		},
		onCancel() {

		},
		onEnd() {

		}
	})

	return <LatchButton controller={latch} data-icon={'\ue86f'}>
		Elementcode Bearbeiten
	</LatchButton>
}

function openEditor(original: HTMLElement): HTMLDialogElement {
	const dialogHost = document.querySelector('#modals')
		?? document.body.appendChild(Object.assign(document.createElement('section'), {
			id: 'modals'
		}));

	const dialog = dialogHost.appendChild(Object.assign(document.createElement('dialog'), {
		id: 'element-editor'
	}));

	let liveNode: HTMLElement = original;
	let committed = false;
	let previewTimeout: number | undefined;

	const parseHTML = (htmlString: string, contextParent: Element): ChildNode[] => {
		const range = contextParent.ownerDocument.createRange();
		range.selectNodeContents(contextParent);
		return [...range.createContextualFragment(htmlString).childNodes];
	};
	const swapLiveNode = (source: string) => {
		const parent = liveNode.parentElement;
		if (!parent) return;

		const nodes = parseHTML(source, parent);
		const [first, ...rest] = nodes;
		if (!first) return;

		liveNode.replaceWith(first, ...rest);
		liveNode = first.nodeType === Node.ELEMENT_NODE ? (first as HTMLElement) : liveNode;
	};

	const restoreOriginalIfNeeded = () => {
		clearTimeout(previewTimeout);
		if (liveNode !== original) {
			liveNode.replaceWith(original);
			liveNode = original;
		}
	};

	const beginPreview = (source: string) => {
		previewTimeout = window.setTimeout(() => swapLiveNode(source), 250);
	};

	const endPreview = () => {
		restoreOriginalIfNeeded();
	};

	const commit = (source: string) => {
		clearTimeout(previewTimeout);
		swapLiveNode(source);
		committed = true;
		dialog.close();
	};

	const root = dom.createRoot(dialog);
	root.render(<EditorForm
		original={original}
		onSubmit={commit}
		onPreviewStart={beginPreview}
		onPreviewEnd={endPreview}/>);

	dialog.addEventListener('close', () => {
		if (!committed) restoreOriginalIfNeeded();
		root.unmount();
		dialog.remove();
	}, {once: true});

	dialog.showModal();

	return dialog;
}

interface EditorFormParams {
	original: HTMLElement;
	onSubmit: (source: string) => void;
	onPreviewStart: (source: string) => void;
	onPreviewEnd: () => void
}

function EditorForm(props: EditorFormParams) {
	const [source, setSource] = React.useState(props.original.outerHTML);

	return <form action={() => props.onSubmit(source)} id="el-editor-content">
		<div className="dialog-header">
			<h1>Element bearbeiten</h1>
		</div>

		<button type='button' className="symbolic close-btn" data-icon={"\ue5cd"}
				// @ts-ignore
				command="request-close"
				commandfor="element-editor"/>

		<ElEditor el={props.original} source={source} onSourceChange={setSource}/>

		<div className="buttons">
			<button
				type="button"
				id="preview-button"
				data-icon={"\ue8f4"}
				onMouseEnter={() => props.onPreviewStart(source)}
				onMouseLeave={() => props.onPreviewEnd()}>Vorschau
			</button>

			<button data-icon={"\ue5cd"}
					// @ts-ignore
					command="close"
					commandfor="element-editor" type="button">
				Abbrechen
			</button>
			<button data-icon={"\uf09b"}>Übernehmen</button>
		</div>
	</form>;
}

function ElEditor(props: { el: HTMLElement; source: string; onSourceChange: (s: string) => void }) {
	const theme = useTheme();

	return <>
		<Breadcrumbs el={props.el}/>
		<ReactCodeMirror value={props.source} onChange={props.onSourceChange} extensions={[html()]} theme={theme}/>
	</>;
}

function useTheme(): typeof dark | typeof light {
	const media = window.matchMedia('(prefers-color-scheme: dark)');

	const [theme, setTheme] = React.useState<typeof light>(media.matches ? dark : light);

	React.useEffect(() => {
		const listener = (e: MediaQueryListEvent) => setTheme(e.matches ? dark : light);
		media.addEventListener('change', listener);
		return () => media.removeEventListener('change', listener);
	}, []);

	return theme;
}

export function Breadcrumbs(props: { el: HTMLElement }) {
	return <div className="breadcrumbs">{getAncestry(props.el)
		.map(i => <code className='breadcrumb-element'>
			<span>{i.tagName.toLowerCase()}</span>
			{i.id && <span className='colour-blue'>#{i.id}</span>}
			{[...i.classList].map(i => <span className='colour-red'>.{i}</span>)}
		</code>)}
	</div>;
}

function getAncestry(el: HTMLElement) {
	const parents = [];

	let current;
	for (current = el; current.parentElement; current = current.parentElement)
		parents.unshift(current);

	parents.unshift(current);

	return parents;
}

export function EverythingEditorButton() {
	const getIframe = React.useContext(iframeCx)!;

	const openEverythingEditor = React.useCallback(() => {
		openEditor(getIframe().contentDocument?.documentElement);
	}, []);

	return <button data-icon={'\uf88c'} onClick={openEverythingEditor}>
		Gesamtes Dokument Bearbeiten
	</button>
}